import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

import ProjectCard from "../components/ProjectCard";
import LogoCarousel from "../components/LogoCarousel";

import email from "../images/email.svg";
import avatar from "../images/avatar.jpg";
import cms from "../images/cms.png";
import nb from "../images/nb/nb0.png";
import ren from "../images/ren/ren06.png";

const Page = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 140px 40px 80px;
  
  @media (max-width: 768px) {
    padding: 100px 20px 60px;
  }
`;

const HeroSection = styled.div`
  margin-bottom: 120px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 40px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const digitalGlitch1 = keyframes`
  0% { clip-path: inset(20% 0 80% 0); transform: translate(-4px, 2px); }
  20% { clip-path: inset(60% 0 10% 0); transform: translate(4px, -2px); }
  40% { clip-path: inset(40% 0 50% 0); transform: translate(-4px, 3px); }
  60% { clip-path: inset(80% 0 5% 0); transform: translate(4px, -3px); }
  80% { clip-path: inset(10% 0 70% 0); transform: translate(-4px, 2px); }
  100% { clip-path: inset(30% 0 50% 0); transform: translate(4px, -2px); }
`;

const digitalGlitch2 = keyframes`
  0% { clip-path: inset(10% 0 60% 0); transform: translate(4px, -2px); }
  20% { clip-path: inset(30% 0 20% 0); transform: translate(-4px, 2px); }
  40% { clip-path: inset(70% 0 10% 0); transform: translate(4px, -3px); }
  60% { clip-path: inset(20% 0 50% 0); transform: translate(-4px, 3px); }
  80% { clip-path: inset(50% 0 30% 0); transform: translate(4px, -2px); }
  100% { clip-path: inset(5% 0 80% 0); transform: translate(-4px, 2px); }
`;

const noise = keyframes`
  0% { background-position: 0 0; }
  20% { background-position: -15% 10%; }
  40% { background-position: 25% -25%; }
  60% { background-position: -25% 15%; }
  80% { background-position: 20% 30%; }
  100% { background-position: -10% -10%; }
`;

const glitchDuration = keyframes`
  0%, 99% { opacity: 0.8; }
  100% { opacity: 0; visibility: hidden; }
`;

const imgGlitch = keyframes`
  0%, 99% { filter: grayscale(100%) contrast(1.5) brightness(1.2); }
  100% { filter: grayscale(100%) contrast(1.1); }
`;

const HeroAvatar = styled.div`
  position: relative;
  width: 380px;
  height: 380px;

  @media (max-width: 1024px) {
    width: 250px;
    height: 250px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    filter: grayscale(100%) contrast(1.1);
    transition: all 0.1s ease;
    display: block;
  }

  &::before, &::after {
    content: "";
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background-image: url(${avatar});
    background-size: cover;
    border-radius: 50%;
    filter: grayscale(100%) contrast(1.8) invert(1);
    opacity: 0;
    pointer-events: none;
    z-index: 2;
  }

  .particles {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    border-radius: 50%;
    background-image: radial-gradient(circle, var(--accent) 1px, transparent 1.5px);
    background-size: 8px 8px;
    opacity: 0;
    pointer-events: none;
    z-index: 3;
    mix-blend-mode: screen;
  }

  &:hover, &.active-glitch {
    cursor: crosshair;

    img {
      animation: ${imgGlitch} 0.1s forwards;
    }
    
    .particles {
      animation: ${noise} 0.1s infinite steps(2), ${glitchDuration} 0.1s forwards;
    }

    &::before {
      left: 10px;
      mix-blend-mode: exclusion;
      animation: ${digitalGlitch1} 0.08s infinite linear alternate-reverse, ${glitchDuration} 0.1s forwards;
    }

    &::after {
      left: -10px;
      animation: ${digitalGlitch2} 0.12s infinite linear alternate-reverse, ${glitchDuration} 0.1s forwards;
    }
  }
`;

const HugeTitle = styled.h1`
  font-size: var(--font-display);
  font-weight: 900;
  line-height: 0.9;
  text-transform: uppercase;
  margin: 0;
  letter-spacing: -0.05em;

  span {
    color: var(--accent);
    display: block;
    max-width: 700px; /* Keep the phrase text from extending and pushing layout */
    transition: opacity 0.4s ease-in-out, transform 0.4s ease-in-out;
    opacity: ${props => (props.$fade ? 1 : 0)};
    transform: ${props => (props.$fade ? "translateY(0)" : "translateY(10px)")};
  }
`;

const SubtitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 60px;
  border-top: 1px solid rgba(150, 150, 150, 0.2);
  padding-top: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }

  p {
    font-size: var(--font-lead);
    max-width: 600px;
    opacity: 0.8;
  }

  .status {
    text-transform: uppercase;
    font-size: var(--font-meta);
    font-weight: 600;
    letter-spacing: 1px;
    text-align: right;
    opacity: 0.6;

    @media (max-width: 768px) {
      text-align: left;
    }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(150, 150, 150, 0.2);
  padding-bottom: 20px;
  margin-bottom: 40px;

  h2 {
    font-size: var(--font-h3);
    font-weight: 800;
    text-transform: uppercase;
    margin: 0;
  }
`;

const WorkGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 80px;
  margin-bottom: 120px;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`;

const PhilosophySection = styled.div`
  background: var(--panel-bg);
  padding: 80px;
  border-radius: 4px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    padding: 40px;
  }
`;

const PhilLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  .tag {
    color: var(--accent);
    font-size: var(--font-meta);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 20px;
    display: block;
  }

  h2 {
    font-size: var(--font-h1);
    font-weight: 900;
    line-height: 1;
    text-transform: uppercase;
    margin-bottom: 30px;
  }

  p {
    font-size: var(--font-body);
    line-height: 1.6;
    opacity: 0.8;
  }
`;

const ContactGrid = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 40px;
  align-items: center;

  a {
    color: #fff;
    text-decoration: none;
    font-weight: 600;
    padding: 15px 30px;
    background: var(--accent);
    border: 1px solid rgba(150, 150, 150, 0.2);
    transition: all 0.2s;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: var(--font-small);
    border-radius: 4px;

    &:hover {
      background: var(--panel-bg);
      color: var(--text-color);
    }
  }
`;

const phrases = [
  { line1: "Digital", line2: "Design" },
  { line1: "Movement", line2: "Direction" },
  { line1: "Film &", line2: "Sound" }
];

const Home = () => {
  const [isGlitching, setIsGlitching] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const handleAvatarTap = () => {
    setIsGlitching(true);
    setTimeout(() => {
      setIsGlitching(false);
    }, 100);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % phrases.length);
        setFade(true);
      }, 400); // Duration matches CSS transition ease
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  return (
    <Page>
      <HeroSection>
        <HeroLeft>
          <HugeTitle $fade={fade}>
            Stephen<br />SJ Brown
            <span>
              {phrases[phraseIndex].line1}<br />
              {phrases[phraseIndex].line2}
            </span>
          </HugeTitle>
          <SubtitleRow>
            <p>i design experiences, move bodies, and create music.</p>
          </SubtitleRow>
          <ContactGrid style={{ marginTop: '30px' }}>
            <a href="mailto:stephen.sjbrown@gmail.com">Get in Touch</a>
          </ContactGrid>
        </HeroLeft>
        <HeroAvatar 
          className={isGlitching ? 'active-glitch' : ''} 
          onClick={handleAvatarTap}
        >
           <img src={avatar} alt="Stephen SJ Brown" />
           <div className="particles" />
        </HeroAvatar>
      </HeroSection>

      <LogoCarousel />

      <div id="work">
        <SectionHeader>
          <h2>SELECTED_WORKS</h2>
        </SectionHeader>
        <WorkGrid>
          <ProjectCard as="div" style={{ cursor: 'default', pointerEvents: 'none' }}>
            <div className="image-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--panel-bg)' }}>
              <img src="https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/google.svg" alt="Google Logo" style={{ width: '80px', height: '80px', objectFit: 'contain', filter: 'grayscale(100%) contrast(0.5)', opacity: 0.3 }} />
            </div>
            <div>
              <div className="project-header">
                <h3 className="project-title">Google</h3>
                <h4 style={{ color: 'var(--text-color)', opacity: 0.5 }}>Private / Releasing Soon</h4>
              </div>
              <p>Upcoming Google project. This work is currently private and will be shown once publically launched.</p>
            </div>
          </ProjectCard>

          <ProjectCard href="/movement/nb">
            <div className="image-wrapper">
              <img src={nb} alt="New Balance" />
            </div>
            <div>
              <div className="project-header">
                <h3 className="project-title">New Balance London</h3>
                <h4>MOVEMENT</h4>
              </div>
              <p>I directed 8 models in total in various poses to create a dynamic flow.</p>
            </div>
          </ProjectCard>

          <ProjectCard href="/movement/renovations">
            <div className="image-wrapper">
              <img src={ren} alt="Renovations" />
            </div>
            <div>
              <div className="project-header">
                <h3 className="project-title">Renovations</h3>
                <h4>DANCE FILM</h4>
              </div>
              <p>A man examines his past. Confronted with the fact that he, his talents, and his relationships have all changed, he searches for a way to navigate the future.</p>
            </div>
          </ProjectCard>

          <ProjectCard href="/design/cms">
            <div className="image-wrapper">
              <img src={cms} alt="Child Maintenance Service" />
            </div>
            <div>
              <div className="project-header">
                <h3 className="project-title">Child Maintenance Service</h3>
                <h4>UX/UI DESIGN</h4>
              </div>
              <p>I helped redesign this online government service.</p>
            </div>
          </ProjectCard>
        </WorkGrid>
      </div>

      <ContactGrid style={{ marginTop: '0', marginBottom: '120px', justifyContent: 'center' }}>
        <a href="mailto:stephen.sjbrown@gmail.com">Get in Touch</a>
      </ContactGrid>
    </Page>
  );
};

export default Home;
